import React, { Fragment } from 'react'
import { NavLink } from 'react-router-dom'
import TouchRipple from '@material-ui/core/ButtonBase'
import MatxVerticalNavExpansionPanel from './MatxVerticalNavExpansionPanel'
import { makeStyles } from '@material-ui/core/styles'
import clsx from 'clsx'
import useSettings from 'app/hooks/useSettings'

const useStyles = makeStyles(({ palette, ...theme }) => ({
    navItem: {
        transition: 'all 150ms ease-in',
        borderTopLeftRadius: 0,
        borderBottomLeftRadius: 0,
        borderTopRightRadius: 10,
        borderBottomRightRadius: 10,
        '&:hover': {
            backgroundColor: '#fafafa',
        },
    },
    navItemActive: {
        backgroundColor: '#fafafa',
    },
    rootNavItem: {
        paddingLeft: 13,
        paddingRight: 8,
        paddingTop: 2,
        paddingBottom: 2,
        marginBottom: 0,
    },
    childNavItem: {
        minHeight: 34,
        height: 34,
        marginLeft: 0,
        marginBottom: 0,
        paddingLeft: 28,
        paddingRight: 8,
        paddingTop: 2,
        paddingBottom: 2,
        backgroundColor: 'transparent',
        borderTopRightRadius: 8,
        borderBottomRightRadius: 8,
        '&:hover': {
            backgroundColor: 'transparent',
        },
        '&:hover $childItemText': {
            color: 'rgba(34, 31, 58, 0.92)',
            fontWeight: 600,
        },
    },
    childNavItemActive: {
        backgroundColor: 'transparent',
        borderRight: 'none',
        '& $childItemText': {
            color: 'rgba(34, 31, 58, 0.96)',
            fontWeight: 600,
        },
    },
    childItemText: {
        fontSize: '0.82rem',
        paddingLeft: 0,
    },
    compactNavItem: {
        overflow: 'hidden',
        '& $itemText': {
            display: 'none',
        },
    },
    compactIcon: {
        width: 24,
        minWidth: 24,
        height: 24,
        lineHeight: '24px',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'visible',
        textAlign: 'center',
        color: '#101010',
        '&.MuiIcon-root': {
            fontSize: 20,
            lineHeight: 1,
            overflow: 'visible',
        },
        '&.material-symbols-outlined': {
            fontVariationSettings: "'FILL' 0, 'wght' 300, 'GRAD' 0, 'opsz' 24",
        },
    },
    navSvgIcon: {
        width: 18,
        height: 18,
        objectFit: 'contain',
        display: 'block',
    },
    navFontIcon: {
        fontFamily: '"Material Symbols Outlined"',
        fontWeight: 400,
        fontStyle: 'normal',
        fontSize: 20,
        lineHeight: 1,
        letterSpacing: 'normal',
        textTransform: 'none',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        whiteSpace: 'nowrap',
        wordWrap: 'normal',
        direction: 'ltr',
        WebkitFontSmoothing: 'antialiased',
        fontVariationSettings: "'FILL' 0, 'wght' 300, 'GRAD' 0, 'opsz' 24",
        color: '#101010',
    },
    itemIcon: {},
    rippleContent: {
        display: 'flex',
        alignItems: 'center',
        height: '100%',
        width: '100%',
    },
    itemText: {
        fontSize: '0.875rem',
        paddingLeft: '0.8rem',
        fontFamily:theme.typography.fontFamily,
        display: 'inline-flex',
        alignItems: 'center',
        lineHeight: 1.2,
    },
    label: {
        color: palette.text.secondary,
    },
    warehouseGroup: {
        margin: '0 8px 0 12px',
        padding: '5px 0 4px 0',
        backgroundColor: 'transparent',
        borderBottom: '1px solid rgba(34, 31, 58, 0.12)',
        '&:last-child': {
            borderBottom: 'none',
        },
        '& $childNavItem': {
            paddingLeft: 12,
        },
        '& $childItemText': {
            fontSize: '0.79rem',
        },
    },
    warehouseGroupLabel: {
        color: 'rgba(34, 31, 58, 0.58)',
        fontSize: '0.68rem',
        fontWeight: 600,
        lineHeight: 1.35,
        letterSpacing: '0.01em',
        margin: '0 8px 2px 12px',
    },
    bulletIcon: {
        background: palette.text.secondary,
    },
}))

const MatxVerticalNav = ({ items, expanded = false }) => {
    const { settings } = useSettings()
    const mode = expanded ? 'full' : settings.layout1Settings.leftSidebar.mode
    const classes = useStyles()

    const renderLevels = (data, level = 0) => {
        return data.map((item, index) => {
            if (item.type === 'warehouseGroup')
                return (
                    <div
                        key={`${item.label}-${index}`}
                        name="child"
                        className={classes.warehouseGroup}
                    >
                        <div className={clsx(classes.warehouseGroupLabel, 'sidenavHoverShow', { hidden: mode === 'compact' })}>{item.label}</div>
                        {renderLevels(item.children || [], level)}
                    </div>
                )
            if (item.type === 'label')
                return (
                    <p
                        key={index}
                        className={clsx({
                            'px-4 mb-2 mt-6 uppercase text-12 sidenavHoverShow': true,
                            [classes.label]: true,
                            hidden: mode === 'compact',
                        })}
                    >
                        {item.label}
                    </p>
                )
            if (item.children) {
                return (
                    <MatxVerticalNavExpansionPanel
                        mode={mode}
                        item={item}
                        key={index}
                    >
                        {renderLevels(item.children, level + 1)}
                    </MatxVerticalNavExpansionPanel>
                )
            } else if (item.type === 'extLink') {
                return (
                    <a
                        key={index}
                        href={item.path}
                            className={clsx({
                                'flex justify-between compactNavItem whitespace-pre': true,
                                'h-44': level === 0,
                                [classes.navItem]: true,
                                [classes.rootNavItem]: level === 0,
                                [classes.childNavItem]: level > 0,
                                [classes.compactNavItem]: mode === 'compact',
                            })}
                        rel="noopener noreferrer"
                        target="_blank"
                    >
                        <TouchRipple
                            key={item.name}
                            name="child"
                            className={clsx('w-full', classes.rippleContent)}
                        >
                            {(() => {
                                if (level > 0) return null
                                if (item.iconSrc) {
                                    return (
                                        <span className={clsx('align-middle', classes.compactIcon)}>
                                            <img src={item.iconSrc} alt="" className={classes.navSvgIcon} />
                                        </span>
                                    )
                                } else if (item.icon) {
                                    return (
                                        <span className={clsx('align-middle', classes.compactIcon, classes.navFontIcon)}>
                                            {item.icon}
                                        </span>
                                    )
                                } else {
                                    return (
                                        <span className="item-icon icon-text">
                                            {item.iconText}
                                        </span>
                                    )
                                }
                            })()}
                            <span
                                className={clsx(
                                    'align-middle sidenavHoverShow',
                                    classes.itemText
                                )}
                            >
                                {item.name}
                            </span>
                            <div className="mx-auto"></div>
                            {item.badge && (
                                <div
                                    className={`rounded bg-${item.badge.color} px-1 py-1px`}
                                >
                                    {item.badge.value}
                                </div>
                            )}
                        </TouchRipple>
                    </a>
                )
            } else {
                if (!item?.path) return null
                return (
                    <NavLink
                        key={index}
                        to={item.path}
                        activeClassName={clsx(classes.navItemActive, level > 0 && classes.childNavItemActive)}
                        className={clsx({
                            'flex justify-between compactNavItem whitespace-pre': true,
                            'h-44': level === 0,
                            [classes.navItem]: true,
                            [classes.rootNavItem]: level === 0,
                            [classes.childNavItem]: level > 0,
                            [classes.compactNavItem]: mode === 'compact',
                        })}
                    >
                        <TouchRipple
                            key={item.name}
                            name="child"
                            className={clsx('w-full', classes.rippleContent)}
                        >
                            {item?.iconSrc ? (
                                level > 0 ? null : (
                                    <span className={clsx('align-middle', classes.compactIcon)}>
                                        <img src={item.iconSrc} alt="" className={classes.navSvgIcon} />
                                    </span>
                                )
                            ) : item?.icon ? (
                                level > 0 ? null : (
                                    <span className={clsx('align-middle', classes.compactIcon, classes.navFontIcon)}>
                                        {item.icon}
                                    </span>
                                )
                            ) : (
                                level > 0 ? null : (
                                    <Fragment>
                                        <div
                                            className={clsx({
                                                'nav-bullet p-2px rounded': true,
                                                'ml-0 mr-0': mode === 'compact',
                                                'ml-8 mr-2': mode !== 'compact',
                                                [classes.bulletIcon]: true,
                                            })}
                                        ></div>
                                        <div
                                            className={clsx({
                                                'nav-bullet-text ml-5 text-11': true,
                                                hidden: mode !== 'compact',
                                            })}
                                        >
                                            {item.iconText}
                                        </div>
                                    </Fragment>
                                )
                            )}
                            <span
                                className={clsx(
                                    'align-middle text-left sidenavHoverShow',
                                    classes.itemText,
                                    level > 0 && classes.childItemText
                                )}
                            >
                                {item.name}
                            </span>
                            <div className="mx-auto"></div>
                            {item.badge && (
                                <div
                                    className={clsx(
                                        `rounded bg-${item.badge.color} px-1 py-1px`,
                                        'sidenavHoverShow',
                                        classes.itemIcon
                                    )}
                                >
                                    {item.badge.value}
                                </div>
                            )}
                        </TouchRipple>
                    </NavLink>
                )
            }
        })
    }

    return <div className="navigation">{renderLevels(items)}</div>
}

export default React.memo(MatxVerticalNav)
